import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { SettingsProvider } from '@/context/SettingsContext';
import { CompanyProvider } from '@/context/CompanyContext';
import { Toaster } from 'sonner';
import ProtectedRoute from '@/components/ProtectedRoute';
import DashboardLayout from '@/components/layout/DashboardLayout';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import LeadsPage from '@/pages/LeadsPage';
import LeadDetailPage from '@/pages/LeadDetailPage';
import CustomersPage from '@/pages/CustomersPage';
import Customer360Page from '@/pages/Customer360Page';
import SegmentsPage from '@/pages/SegmentsPage';
import SegmentBuilderPage from '@/pages/SegmentBuilderPage';
import StaticListDetailPage from '@/pages/StaticListDetailPage';
import TemplatesHubPage from '@/pages/TemplatesHubPage';
import EmailTemplateEditorPage from '@/pages/EmailTemplateEditorPage';
import BroadcastsListPage from '@/pages/BroadcastsListPage';
import BroadcastWizardPage from '@/pages/BroadcastWizardPage';
import InventoryMasterPage from '@/pages/InventoryMasterPage';
import ChannelListingsPage from '@/pages/ChannelListingsPage';
import InventorySettingsPage from '@/pages/InventorySettingsPage';
import InwardingPage from '@/pages/InwardingPage';
import VendorMasterPage from '@/pages/VendorMasterPage';
import VendorDetailPage from '@/pages/VendorDetailPage';
import VendorPickupsPage from '@/pages/VendorPickupsPage';
import MarketplacePickupsPage from '@/pages/MarketplacePickupsPage';
import GoodsReceiptPage from '@/pages/GoodsReceiptPage';
import POLabelsPage from '@/pages/POLabelsPage';
import PoScanPage from '@/pages/PoScanPage';
import DeliveriesPage from '@/pages/DeliveriesPage';
import DeliveryTrackSheetPage from '@/pages/DeliveryTrackSheetPage';
import OrdersPage from '@/pages/OrdersPage';
import CashManagementPage from '@/pages/CashManagementPage';
import POSPage from '@/pages/POSPage';
import POSSetupPage from '@/pages/POSSetupPage';
import POSOrdersPage from '@/pages/POSOrdersPage';
import ExpensesPage from '@/pages/ExpensesPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import MyExpensesPage from '@/pages/MyExpensesPage';
import IncentivesPage from '@/pages/IncentivesPage';
import MySalesDashboardPage from '@/pages/MySalesDashboardPage';
import SalesPerformancePage from '@/pages/SalesPerformancePage';
import SalesTargetsPage from '@/pages/SalesTargetsPage';
import DeliveryIncentivesPage from '@/pages/DeliveryIncentivesPage';
import ProductAvailabilityPage from '@/pages/ProductAvailabilityPage';
import AnnouncementsPage from '@/pages/AnnouncementsPage';
import ProfilePage from '@/pages/ProfilePage';
import UserManagementPage from '@/pages/UserManagementPage';
import RoleManagementPage from '@/pages/RoleManagementPage';
import SettingsPage from '@/pages/SettingsPage';
import NotificationsPage from '@/pages/NotificationsPage';
import ShopifyIntegrationPage from '@/pages/ShopifyIntegrationPage';
import FlipkartIntegrationPage from '@/pages/FlipkartIntegrationPage';
import AmazonMarketplacesHub from '@/pages/AmazonMarketplacesHub';
import AmazonIntegrationPage from '@/pages/AmazonIntegrationPage';
import IntegrationsPage from '@/pages/IntegrationsPage';
import MamoPayIntegrationPage from '@/pages/MamoPayIntegrationPage';
import ZohoIntegrationPage from '@/pages/ZohoIntegrationPage';
import WhatsAppIntegrationPage from '@/pages/WhatsAppIntegrationPage';
import MarketingJourneysPage from '@/pages/MarketingJourneysPage';
import PushNotificationsPage from '@/pages/PushNotificationsPage';
import JourneyBuilderPage from '@/pages/JourneyBuilderPage';
import InboxPage from '@/pages/InboxPage';
import InboxSettingsPage from '@/pages/InboxSettingsPage';
import AICopilotSettingsPage from '@/pages/AICopilotSettingsPage';
import InventoryDiagnosticPage from '@/pages/InventoryDiagnosticPage';
import PurchaseOrdersPage from '@/pages/PurchaseOrdersPage';
import PublicPOViewPage from '@/pages/PublicPOViewPage';
import AuditLogPage from '@/pages/AuditLogPage';
import DriverOrdersPage from '@/pages/DriverOrdersPage';
import DriverPerformancePage from '@/pages/DriverPerformancePage';
// HR Module Pages
import HRDashboardPage from '@/pages/HRDashboardPage';
import EmployeeManagementPage from '@/pages/EmployeeManagementPage';
import AttendanceMarkingPage from '@/pages/AttendanceMarkingPage';
import AttendanceRecordsPage from '@/pages/AttendanceRecordsPage';
import AttendanceMapPage from '@/pages/AttendanceMapPage';
import AbsentTodayPage from '@/pages/AbsentTodayPage';
import LeaveManagementPage from '@/pages/LeaveManagementPage';
import HolidayManagementPage from '@/pages/HolidayManagementPage';
import PayrollManagementPage from '@/pages/PayrollManagementPage';
import HRSettingsPage from '@/pages/HRSettingsPage';
import HRReportsPage from '@/pages/HRReportsPage';
import APIKeysPage from '@/pages/APIKeysPage';
import VendorPortalLoginPage from '@/pages/VendorPortalLoginPage';
import VendorPortalDashboardPage from '@/pages/VendorPortalDashboardPage';
// Marketplace Vendor Portal (Phase 1, May 2026)
import MarketplaceOnboardingPage from '@/pages/MarketplaceOnboardingPage';
import MarketplaceVendorLoginPage from '@/pages/MarketplaceVendorLoginPage';
import MarketplaceVendorDashboardPage from '@/pages/MarketplaceVendorDashboardPage';
import MarketplaceAdminPage from '@/pages/MarketplaceAdminPage';
import MarketplaceVendorDetailPage from '@/pages/MarketplaceVendorDetailPage';
// Super Admin Pages
import SuperAdminDashboardPage from '@/pages/SuperAdminDashboardPage';
import SuperAdminCompaniesPage from '@/pages/SuperAdminCompaniesPage';
import SuperAdminCompanySettingsPage from '@/pages/SuperAdminCompanySettingsPage';
import SuperAdminUsersPage from '@/pages/SuperAdminUsersPage';
import SuperAdminIntegrationsPage from '@/pages/SuperAdminIntegrationsPage';
import SuperAdminPlatformCredentialsPage from '@/pages/SuperAdminPlatformCredentialsPage';
import SuperAdminBillingPage from '@/pages/SuperAdminBillingPage';
import StripeSettingsPage from '@/pages/StripeSettingsPage';
import CompanyOnboardingPage from '@/pages/CompanyOnboardingPage';
import CompanyBillingPage from '@/pages/CompanyBillingPage';
// Shopify Billing & Onboarding Pages
import MagicLoginPage from '@/pages/MagicLoginPage';
import OnboardingPage from '@/pages/OnboardingPage';
import BillingPage from '@/pages/BillingPage';
import ShopifyInstallPage from '@/pages/ShopifyInstallPage';
import DirectOnboardPage from '@/pages/DirectOnboardPage';
import WarehousesPage from '@/pages/WarehousesPage';
import AbandonedCheckoutsPage from '@/pages/AbandonedCheckoutsPage';
import OrderTrackingPage from '@/pages/OrderTrackingPage';
import TrackingLookupPage from '@/pages/TrackingLookupPage';
import TrackingSettingsPage from '@/pages/TrackingSettingsPage';
import PaymentMethodsSettingsPage from '@/pages/PaymentMethodsSettingsPage';
import NoonIntegrationPage from '@/pages/NoonIntegrationPage';
import NoonPublishPage from '@/pages/NoonPublishPage';
import DanubeHomeIntegrationPage from '@/pages/DanubeHomeIntegrationPage';
import HomeCentreIntegrationPage from '@/pages/HomeCentreIntegrationPage';
import CarrefourIntegrationPage from '@/pages/CarrefourIntegrationPage';
import StripeIntegrationPage from '@/pages/StripeIntegrationPage';
import DelhiveryIntegrationPage from '@/pages/DelhiveryIntegrationPage';
import BluedartIntegrationPage from '@/pages/BluedartIntegrationPage';
import DTDCIntegrationPage from '@/pages/DTDCIntegrationPage';
import CustomCourierIntegrationPage from '@/pages/CustomCourierIntegrationPage';
import CourierSettingsPage from '@/pages/CourierSettingsPage';
import ShippingRateCardsPage from '@/pages/ShippingRateCardsPage';
import ShiprocketIntegrationPage from '@/pages/ShiprocketIntegrationPage';
import CashReportsPage from '@/pages/CashReportsPage';
import NDRDashboardPage from '@/pages/NDRDashboardPage';
import DocumentsHubPage from '@/pages/DocumentsHubPage';
import PackagesPage from '@/pages/PackagesPage';
import InstagramIntegrationPage from '@/pages/InstagramIntegrationPage';
import InstagramChatbotPage from '@/pages/InstagramChatbotPage';
import InstagramPostPage from '@/pages/InstagramPostPage';
import ReturnOrdersPage from '@/pages/ReturnOrdersPage';
import ReturnRulesPage from '@/pages/ReturnRulesPage';
import DeliveryZonesPage from '@/pages/DeliveryZonesPage';
import LabelSettingsPage from '@/pages/LabelSettingsPage';
import ReturnInwardingPage from '@/pages/ReturnInwardingPage';
import CustomerReturnPortal from '@/pages/CustomerReturnPortal';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import MissingInventoryPage from '@/pages/MissingInventoryPage';
import EmailIntegrationPage from '@/pages/EmailIntegrationPage';
import EmailCampaignsPage from '@/pages/EmailCampaignsPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import SuperAdminRoutes from '@/superadmin/SuperAdminRoutes';
import '@/App.css';

function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <CompanyProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              
              {/* Password reset (public) */}
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              
              {/* Super Admin Portal (separate auth, separate layout) */}
              <Route path="/superadmin/*" element={<SuperAdminRoutes />} />
              
              {/* Public Shopify Install & Magic Login Routes */}
              <Route path="/shopify-install" element={<ShopifyInstallPage />} />
              <Route path="/magic-login" element={<MagicLoginPage />} />
              
              {/* Direct Onboarding - Public Route */}
              <Route path="/onboard" element={<DirectOnboardPage />} />
              
              {/* Public Order Tracking Page - No Auth Required */}
              <Route path="/track" element={<TrackingLookupPage />} />
              <Route path="/track/:token" element={<OrderTrackingPage />} />
              
              {/* Public Customer Return Portal - No Auth Required */}
              <Route path="/returns" element={<CustomerReturnPortal />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/payment-cancelled" element={<PaymentSuccessPage />} />
              
              {/* Onboarding (requires auth but no layout) */}
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              } />
              
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                
                {/* Billing Page */}
                <Route path="billing" element={<BillingPage />} />
                
                {/* Super Admin Routes */}
                <Route path="super-admin" element={<SuperAdminDashboardPage />} />
                <Route path="super-admin/companies" element={<SuperAdminCompaniesPage />} />
                <Route path="super-admin/companies/:companyId" element={<SuperAdminCompaniesPage />} />
                <Route path="super-admin/companies/:companyId/settings" element={<SuperAdminCompanySettingsPage />} />
                <Route path="super-admin/companies/:companyId/billing" element={<CompanyBillingPage />} />
                <Route path="super-admin/users" element={<SuperAdminUsersPage />} />
                <Route path="super-admin/integrations" element={<SuperAdminIntegrationsPage />} />
                <Route path="super-admin/platform-credentials" element={<SuperAdminPlatformCredentialsPage />} />
                <Route path="super-admin/billing" element={<SuperAdminBillingPage />} />
                <Route path="super-admin/stripe-settings" element={<StripeSettingsPage />} />
                <Route path="super-admin/onboarding" element={<CompanyOnboardingPage />} />
                
              {/* Dashboard - accessible to all logged in users */}
              <Route path="dashboard" element={<DashboardPage />} />
              
              {/* Driver-specific mobile-optimized page */}
              <Route 
                path="my-deliveries" 
                element={
                  <ProtectedRoute requiredPermission="nav.my_deliveries">
                    <DriverOrdersPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Protected by nav.leads permission */}
              <Route
                path="leads"
                element={
                  <ProtectedRoute requiredPermission="nav.leads">
                    <LeadsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="leads/:id"
                element={
                  <ProtectedRoute requiredPermission="nav.leads">
                    <LeadDetailPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Customers page */}
              <Route
                path="customers"
                element={
                  <ProtectedRoute requiredPermission="nav.orders">
                    <CustomersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="customers/360"
                element={
                  <ProtectedRoute requiredPermission="nav.orders">
                    <Customer360Page />
                  </ProtectedRoute>
                }
              />
              <Route
                path="customers/segments"
                element={
                  <ProtectedRoute requiredPermission="nav.orders">
                    <SegmentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="customers/segments/builder/:segmentId"
                element={
                  <ProtectedRoute requiredPermission="nav.orders">
                    <SegmentBuilderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="customers/segments/static-lists/:listId"
                element={
                  <ProtectedRoute requiredPermission="nav.orders">
                    <StaticListDetailPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.inventory permission */}
              <Route
                path="inventory"
                element={
                  <ProtectedRoute requiredPermission="nav.inventory">
                    <InventoryMasterPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="inventory/channel-listings"
                element={
                  <ProtectedRoute requiredPermission="nav.inventory">
                    <ChannelListingsPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Inventory Settings - requires settings permission */}
              <Route
                path="inventory/settings"
                element={
                  <ProtectedRoute requiredPermission="inventory.settings">
                    <InventorySettingsPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Inventory Warehouses - requires warehouses view permission */}
              <Route
                path="inventory/warehouses"
                element={
                  <ProtectedRoute requiredPermission="inventory.warehouses.view">
                    <WarehousesPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Packages - requires packages view permission */}
              <Route
                path="inventory/packages"
                element={
                  <ProtectedRoute requiredPermission="inventory.packages.view">
                    <PackagesPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Inventory Inwarding - requires inwarding permission */}
              <Route
                path="inventory/inwarding"
                element={
                  <ProtectedRoute requiredPermission="inventory.inwarding">
                    <InwardingPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.orders permission */}
              <Route
                path="orders"
                element={
                  <ProtectedRoute requiredPermission="nav.orders">
                    <OrdersPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Abandoned Checkouts - Under Orders */}
              <Route
                path="orders/abandoned"
                element={
                  <ProtectedRoute requiredPermission="abandoned_checkouts.view">
                    <AbandonedCheckoutsPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Return Orders - Under Orders */}
              <Route
                path="orders/returns"
                element={
                  <ProtectedRoute requiredPermission="nav.orders">
                    <ReturnOrdersPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Return Rules - Under Settings */}
              <Route
                path="settings/return-rules"
                element={
                  <ProtectedRoute requiredPermission="returns.rules.view">
                    <ReturnRulesPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Delivery ETA - Under Settings */}
              <Route
                path="settings/delivery-eta"
                element={
                  <ProtectedRoute requiredPermission="nav.settings">
                    <DeliveryZonesPage />
                  </ProtectedRoute>
                }
              />
              {/* SKU Label Settings - Under Settings */}
              <Route
                path="settings/labels"
                element={
                  <ProtectedRoute requiredPermission="nav.settings">
                    <LabelSettingsPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Return Inwarding - Under Orders */}
              <Route
                path="orders/inwarding"
                element={
                  <ProtectedRoute requiredPermission="returns.inwarding.view">
                    <ReturnInwardingPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Missing Inventory Claims - Under Orders */}
              <Route
                path="orders/missing-inventory"
                element={
                  <ProtectedRoute requiredPermission="returns.missing_inventory.view">
                    <MissingInventoryPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.deliveries permission */}
              <Route
                path="deliveries"
                element={
                  <ProtectedRoute requiredPermission="nav.deliveries">
                    <DeliveriesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="delivery-track-sheet"
                element={
                  <ProtectedRoute requiredPermission="nav.deliveries">
                    <DeliveryTrackSheetPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.vendors permission */}
              <Route
                path="vendors"
                element={
                  <ProtectedRoute requiredPermission="nav.vendors">
                    <VendorMasterPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Vendor Detail - Performance & Ledger */}
              <Route
                path="vendors/:vendorId"
                element={
                  <ProtectedRoute requiredPermission="nav.vendors">
                    <VendorDetailPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.purchase_orders permission */}
              <Route
                path="purchase-orders"
                element={
                  <ProtectedRoute requiredPermission="nav.purchase_orders">
                    <PurchaseOrdersPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Vendor Pickups - accessible to drivers and admins */}
              <Route
                path="vendor-pickups"
                element={
                  <ProtectedRoute requiredPermission="nav.deliveries">
                    <VendorPickupsPage />
                  </ProtectedRoute>
                }
              />

              {/* Marketplace Pickups — dedicated driver page with QR scanner
                  for marketplace vendor PO pickups (Feb 2026). */}
              <Route
                path="marketplace-pickups"
                element={
                  <ProtectedRoute requiredPermission="marketplace.pickups.driver">
                    <MarketplacePickupsPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Goods Receipt Note - warehouse verification */}
              <Route
                path="purchase-orders/:poId/grn"
                element={
                  <ProtectedRoute requiredPermission="nav.purchase_orders">
                    <GoodsReceiptPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.tasks permission */}
              <Route
                path="tasks"
                element={
                  <ProtectedRoute requiredPermission="nav.tasks">
                    <div className="text-wt-text-primary">Tasks - Coming Soon</div>
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.marketing permission */}
              <Route
                path="marketing"
                element={
                  <ProtectedRoute requiredPermission="nav.marketing">
                    <div className="text-wt-text-primary">Marketing Dashboard - Coming Soon</div>
                  </ProtectedRoute>
                }
              />
              
              {/* Profile and Notifications - always accessible */}
              <Route path="profile" element={<ProfilePage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              
              {/* Protected by nav.users permission */}
              <Route
                path="users"
                element={
                  <ProtectedRoute requiredPermission="nav.users">
                    <UserManagementPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.roles permission */}
              <Route
                path="roles"
                element={
                  <ProtectedRoute requiredPermission="nav.roles">
                    <RoleManagementPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.settings permission */}
              <Route
                path="settings"
                element={
                  <ProtectedRoute requiredPermission="nav.settings">
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.shopify permission */}
              <Route
                path="integrations/shopify"
                element={
                  <ProtectedRoute requiredPermission="nav.shopify">
                    <ShopifyIntegrationPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="integrations/flipkart"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <FlipkartIntegrationPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="integrations/amazon"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <AmazonMarketplacesHub />
                  </ProtectedRoute>
                }
              />

              <Route
                path="integrations/amazon/:country"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <AmazonIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Integrations Hub */}
              <Route
                path="integrations"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <IntegrationsPage />
                  </ProtectedRoute>
                }
              />
              
              {/* MamoPay Integration */}
              <Route
                path="integrations/mamopay"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <MamoPayIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Zoho Books Integration */}
              <Route
                path="integrations/zoho"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <ZohoIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              {/* WhatsApp Business Integration */}
              <Route
                path="integrations/whatsapp"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <WhatsAppIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Instagram Integration */}
              <Route
                path="integrations/instagram"
                element={
                  <ProtectedRoute requiredPermission="marketing.instagram.manage">
                    <InstagramIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Instagram Chatbot - Under Marketing */}
              <Route
                path="marketing/instagram/chatbot"
                element={
                  <ProtectedRoute requiredPermission="marketing.instagram.manage">
                    <InstagramChatbotPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Instagram Posts - Under Marketing */}
              <Route
                path="marketing/instagram/post"
                element={
                  <ProtectedRoute requiredPermission="marketing.instagram.send">
                    <InstagramPostPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Stripe Integration */}
              <Route
                path="integrations/stripe"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <StripeIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Delhivery Courier Integration */}
              <Route
                path="integrations/delhivery"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <DelhiveryIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="integrations/bluedart"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <BluedartIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="integrations/dtdc"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <DTDCIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="integrations/shiprocket"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <ShiprocketIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Custom Courier Integration */}
              <Route
                path="integrations/custom-courier"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <CustomCourierIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Courier Settings (Serviceability & Rules) */}
              <Route
                path="integrations/courier-settings"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <CourierSettingsPage />
                  </ProtectedRoute>
                }
              />

              {/* Shipping Rate Cards (per-courier zones/slabs/surcharges) */}
              <Route
                path="integrations/shipping-rate-cards"
                element={
                  <ProtectedRoute requiredPermission="nav.integrations">
                    <ShippingRateCardsPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Email Integration */}
              <Route
                path="integrations/email"
                element={
                  <ProtectedRoute requiredPermission="marketing.email.manage">
                    <EmailIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Email Campaigns — DEPRECATED, redirect to Broadcasts */}
              <Route
                path="marketing/email-campaigns"
                element={<Navigate to="/marketing/broadcasts" replace />}
              />
              
              {/* NDR Dashboard */}
              <Route
                path="ndr"
                element={
                  <ProtectedRoute requiredPermission="nav.orders">
                    <NDRDashboardPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Documents Hub */}
              <Route
                path="documents"
                element={
                  <ProtectedRoute requiredPermission="nav.orders">
                    <DocumentsHubPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Tracking Page Settings */}
              <Route
                path="settings/tracking"
                element={
                  <ProtectedRoute requiredPermission="nav.settings">
                    <TrackingSettingsPage />
                  </ProtectedRoute>
                }
              />

              {/* Payment Methods Settings */}
              <Route
                path="settings/payment-methods"
                element={
                  <ProtectedRoute requiredPermission="nav.settings">
                    <PaymentMethodsSettingsPage />
                  </ProtectedRoute>
                }
              />

              {/* noon Marketplace Integration */}
              <Route
                path="settings/noon"
                element={
                  <ProtectedRoute requiredPermission="nav.settings">
                    <NoonIntegrationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/noon/publish"
                element={
                  <ProtectedRoute requiredPermission="nav.settings">
                    <NoonPublishPage />
                  </ProtectedRoute>
                }
              />

              {/* Danube Home Marketplace Integration */}
              <Route
                path="settings/danube"
                element={
                  <ProtectedRoute requiredPermission="nav.settings">
                    <DanubeHomeIntegrationPage />
                  </ProtectedRoute>
                }
              />

              {/* Home Centre Marketplace Integration */}
              <Route
                path="settings/homecentre"
                element={
                  <ProtectedRoute requiredPermission="nav.settings">
                    <HomeCentreIntegrationPage />
                  </ProtectedRoute>
                }
              />

              {/* Carrefour UAE Marketplace Integration */}
              <Route
                path="settings/carrefour"
                element={
                  <ProtectedRoute requiredPermission="nav.settings">
                    <CarrefourIntegrationPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Marketing Journeys */}
              <Route
                path="marketing/journeys"
                element={
                  <ProtectedRoute requiredPermission="marketing.journeys.view">
                    <MarketingJourneysPage />
                  </ProtectedRoute>
                }
              />

              {/* Marketing Push Notifications */}
              <Route
                path="marketing/push"
                element={
                  <ProtectedRoute requiredPermission="marketing.broadcasts.view">
                    <PushNotificationsPage />
                  </ProtectedRoute>
                }
              />

              {/* Marketing Templates (Email + WhatsApp) */}
              <Route
                path="marketing/templates"
                element={
                  <ProtectedRoute requiredPermission="marketing.journeys.view">
                    <TemplatesHubPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="marketing/templates/email/:templateId"
                element={
                  <ProtectedRoute requiredPermission="marketing.journeys.view">
                    <EmailTemplateEditorPage />
                  </ProtectedRoute>
                }
              />

              {/* Marketing Broadcasts */}
              <Route
                path="marketing/broadcasts"
                element={
                  <ProtectedRoute requiredPermission="marketing.broadcasts.view">
                    <BroadcastsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="marketing/broadcasts/new"
                element={
                  <ProtectedRoute requiredPermission="marketing.broadcasts.create">
                    <BroadcastWizardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="marketing/broadcasts/:broadcastId"
                element={
                  <ProtectedRoute requiredPermission="marketing.broadcasts.view">
                    <BroadcastWizardPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Visual Journey Builder */}
              <Route
                path="marketing/journeys/builder/:journeyId"
                element={
                  <ProtectedRoute requiredPermission="marketing.journeys.view">
                    <JourneyBuilderPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Legacy route for backwards compatibility */}
              <Route
                path="marketing/journeys/:journeyId"
                element={
                  <ProtectedRoute requiredPermission="marketing.journeys.view">
                    <JourneyBuilderPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Marketing Inbox */}
              <Route
                path="marketing/inbox"
                element={
                  <ProtectedRoute requiredPermission="marketing.inbox.view">
                    <InboxPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Inbox Settings */}
              <Route
                path="marketing/inbox/settings"
                element={
                  <ProtectedRoute requiredPermission="marketing.inbox.manage">
                    <InboxSettingsPage />
                  </ProtectedRoute>
                }
              />

              {/* AI Sales Copilot Settings */}
              <Route
                path="ai-copilot/settings"
                element={
                  <ProtectedRoute requiredPermission="marketing.inbox.manage">
                    <AICopilotSettingsPage />
                  </ProtectedRoute>
                }
              />

              {/* One-shot read-only inventory drift diagnostic.
                  Admin only — surfaces a download button instead of
                  forcing the operator to hit the API URL directly
                  (which fails auth in a fresh browser tab because the
                  JWT lives in localStorage, not cookies). */}
              <Route
                path="diagnostics/inventory-csv"
                element={
                  <ProtectedRoute>
                    <InventoryDiagnosticPage />
                  </ProtectedRoute>
                }
              />
              
              {/* API Keys - Admin only */}
              <Route
                path="api-keys"
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <APIKeysPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.cash permission */}
              <Route
                path="pos"
                element={
                  <ProtectedRoute requiredPermission="nav.pos">
                    <POSPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="pos/setup"
                element={
                  <ProtectedRoute requiredPermission="nav.pos">
                    <POSSetupPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="pos/orders"
                element={
                  <ProtectedRoute requiredPermission="nav.pos">
                    <POSOrdersPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="cash"
                element={
                  <ProtectedRoute requiredPermission="nav.cash">
                    <CashManagementPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="cash/reports"
                element={
                  <ProtectedRoute requiredPermission="nav.cash">
                    <CashReportsPage />
                  </ProtectedRoute>
                }
              />

              {/* Expenses module — Feb 14, 2026 */}
              <Route
                path="analytics"
                element={
                  <ProtectedRoute requiredPermission="nav.dashboard">
                    <AnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="expenses"
                element={
                  <ProtectedRoute requiredPermission="nav.expenses">
                    <ExpensesPage />
                  </ProtectedRoute>
                }
              />

              {/* Employee My Expenses — Phase 2, Feb 18, 2026 */}
              <Route
                path="my-expenses"
                element={
                  <ProtectedRoute>
                    <MyExpensesPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.incentives permission */}
              <Route
                path="incentives"
                element={
                  <ProtectedRoute requiredPermission="nav.incentives">
                    <IncentivesPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="my-sales"
                element={
                  <ProtectedRoute requiredPermission="incentives.performance_view_own">
                    <MySalesDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sales-performance"
                element={
                  <ProtectedRoute requiredPermission="incentives.performance_view_all">
                    <SalesPerformancePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sales-targets"
                element={
                  <ProtectedRoute requiredPermission="incentives.targets_view">
                    <SalesTargetsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="delivery-incentives"
                element={
                  <ProtectedRoute requiredPermission="incentives.manage">
                    <DeliveryIncentivesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="product-availability"
                element={
                  <ProtectedRoute requiredPermission="nav.product_availability">
                    <ProductAvailabilityPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected by nav.announcements permission */}
              <Route
                path="announcements"
                element={
                  <ProtectedRoute requiredPermission="nav.announcements">
                    <AnnouncementsPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Audit Log - accessible to admins */}
              <Route
                path="audit-log"
                element={
                  <ProtectedRoute requiredPermission="nav.settings">
                    <AuditLogPage />
                  </ProtectedRoute>
                }
              />
              
              {/* HR Module Routes */}
              <Route
                path="hr"
                element={
                  <ProtectedRoute requiredPermission="nav.hr">
                    <HRDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="hr/employees"
                element={
                  <ProtectedRoute requiredPermission="hr.view_employees">
                    <EmployeeManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="hr/mark-attendance"
                element={
                  <ProtectedRoute requiredPermission="hr.mark_attendance">
                    <AttendanceMarkingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="hr/attendance"
                element={
                  <ProtectedRoute requiredPermission="hr.view_attendance">
                    <AttendanceRecordsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="hr/absent-today"
                element={
                  <ProtectedRoute requiredPermission="hr.view_attendance">
                    <AbsentTodayPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="hr/attendance-map"
                element={
                  <ProtectedRoute requiredPermission="hr.view_attendance">
                    <AttendanceMapPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="driver-performance"
                element={
                  <ProtectedRoute requiredPermission="orders.view">
                    <DriverPerformancePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="hr/leaves"
                element={
                  <ProtectedRoute requiredPermission="hr.view_leaves">
                    <LeaveManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="hr/holidays"
                element={
                  <ProtectedRoute requiredPermission="hr.manage_holidays">
                    <HolidayManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="hr/payroll"
                element={
                  <ProtectedRoute requiredPermission="hr.view_payroll">
                    <PayrollManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="hr/settings"
                element={
                  <ProtectedRoute requiredPermission="hr.manage_attendance_config">
                    <HRSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="hr/reports"
                element={
                  <ProtectedRoute requiredPermission="hr.view_reports">
                    <HRReportsPage />
                  </ProtectedRoute>
                }
              />

              {/* Marketplace admin — kept inside DashboardLayout so it
                  inherits the standard sidebar/header. Feb 2026: UX
                  consolidation request from the merchant. */}
              <Route
                path="marketplace-admin"
                element={
                  <ProtectedRoute requiredPermission="nav.marketplace_admin">
                    <MarketplaceAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="marketplace-admin/vendors/:vendorId"
                element={
                  <ProtectedRoute requiredPermission="marketplace.vendors.view">
                    <MarketplaceVendorDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* Catch-all: unknown paths (e.g. stale notification links)
                  redirect to dashboard instead of rendering a blank page */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
            
            {/* Public PO View (no auth required) */}
            <Route path="/po/view/:shareToken" element={<PublicPOViewPage />} />
            
            {/* Vendor Portal (separate auth) */}
            <Route path="/vendor-portal" element={<VendorPortalLoginPage />} />
            <Route path="/vendor-portal/dashboard" element={<VendorPortalDashboardPage />} />
            <Route path="/vendor-portal/labels/:poId" element={<POLabelsPage mode="vendor" />} />

            {/* PO label QR landing — works for staff (JWT) and vendors (session) */}
            <Route path="/po-scan/:poId" element={<PoScanPage />} />
            <Route path="/po-labels/:poId" element={<POLabelsPage mode="staff" />} />

            {/* Marketplace Vendor Portal — public onboarding + isolated vendor app */}
            <Route path="/marketplace/onboarding/:companyId" element={<MarketplaceOnboardingPage />} />
            <Route path="/marketplace-vendor/login" element={<MarketplaceVendorLoginPage />} />
            <Route path="/marketplace-vendor/login/:companyId" element={<MarketplaceVendorLoginPage />} />
            <Route path="/marketplace-vendor/dashboard" element={<MarketplaceVendorDashboardPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#121214',
              border: '1px solid #27272A',
              color: '#FAFAFA',
            },
          }}
        />
        </CompanyProvider>
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App;
